import { getApiBaseUrl } from '@/config/apiConfig'
import { isNativePlatform } from '@/lib/platform/platformDetection'

/**
 * Lightweight connectivity probe that verifies actual server reachability.
 *
 * navigator.onLine is unreliable — it can report "online" behind captive
 * portals, dead WiFi, or when the server is down. This module does a real
 * HEAD request to /version.json (static, no auth, always available) to
 * confirm the server is reachable.
 *
 * This is the ONE reachability answer in the app: the offline queue, the
 * deferred media purge (`mediaAccountBoundary.ts`), the chunk-error handler and
 * the prefetch gate all ask it, so there is no second probe polling alongside it.
 *
 * Web: a bare `/version.json` is same-origin and comes from the server, and
 * `response.ok` is the answer.
 *
 * Native: the page origin (`capacitor://localhost` / `https://localhost`) is
 * served from the app bundle, so a bare `/version.json` succeeds on site WiFi
 * with no uplink and the probe reduced to `navigator.onLine`. Native therefore
 * asks the API host: HEAD `/version.json` on `getApiBaseUrl()`, the static file
 * `linkLatencyProbe.ts` already measures. It is served by the PWA container
 * behind Caddy and never reaches PHP, so it cannot wait on a session lock.
 * - `mode: 'no-cors'`: the API host is cross-origin and a static file carries no
 *   CORS headers. The opaque response hides the status, but any response at all
 *   means an HTTPS round-trip to the real API host completed — a captive portal
 *   or a network with no uplink fails before that (DNS, TCP or the certificate).
 * - The API host must be HTTPS. A captive portal can answer plain HTTP, and then
 *   "any response" would confirm nothing. Without a configured HTTPS API host the
 *   probe reports unreachable.
 * - No `navigator.onLine` short-cut: in WKWebView it can stay false after the
 *   network is back (`main.tsx` wires React Query to Capacitor's Network plugin
 *   for that reason). A device that is really offline fails the request at once.
 *
 * Results are cached briefly (CACHE_TTL_MS) and concurrent callers share one
 * request, to avoid rapid-fire probes when multiple events fire simultaneously
 * (focus + online + visible, or every route loader while the media purge is owed).
 */

const PROBE_TIMEOUT_MS = 5000
const CACHE_TTL_MS = 5000

const PROBE_PATH = '/version.json'

let cachedResult: boolean | null = null
let cachedAt = 0
let inFlight: Promise<boolean> | null = null
// Bumped by `invalidateProbeCache`, so a request started before it cannot refill the cache.
let generation = 0

/**
 * An abort signal that fires after `ms`, built from AbortController rather than
 * AbortSignal.timeout.
 *
 * Not stylistic. `AbortSignal.timeout` needs Chrome 103, and this file's whole
 * job is deciding whether the app is online — on an engine without it, the call
 * threw INSIDE probeConnectivity's try, the catch read that as "server
 * unreachable", and the app concluded it was permanently offline.
 *
 * What that costs, named precisely rather than as "the queue stops": the
 * RECONNECT path is the casualty. useNativeQueueAutoSync consults this probe
 * before syncing on a network/app event, so work queued while genuinely offline
 * is never retried once the connection returns. usePrefetchIssues and
 * chunkErrorHandler gate on it too. The immediate post-enqueue attempt in
 * api-client survives, because it asks Capacitor's getNetworkStatus instead —
 * so the failure is invisible to anyone testing while already online.
 *
 * That engine is not hypothetical: production telemetry found an Android 11
 * (API 30) device — four API levels above minSdk — running System WebView pinned
 * to Chrome/90, because System WebView is a separately Play-Store-updated APK.
 * minSdk constrains the OS, not the engine. See .browserslistrc, which records
 * the same finding.
 *
 * AbortController + setTimeout works everywhere the app runs. The timer is
 * cancelled by the caller so a fast response does not leave it pending.
 */
function timeoutSignal(ms: number): { signal: AbortSignal; cancel: () => void } {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), ms)

  return { signal: controller.signal, cancel: () => clearTimeout(timer) }
}

/**
 * The API host's `/version.json`, or `null` when no HTTPS API host is configured.
 */
function apiHostProbeUrl(): string | null {
  let base = ''
  try {
    base = getApiBaseUrl()
  } catch {
    // Dev-only throw on native without VITE_API_URL.
  }
  if (!base) return null
  try {
    const url = new URL(PROBE_PATH, base)
    return url.protocol === 'https:' ? url.toString() : null
  } catch {
    return null
  }
}

async function nativeReachable(): Promise<boolean> {
  const url = apiHostProbeUrl()
  if (url === null) return false

  const timeout = timeoutSignal(PROBE_TIMEOUT_MS)
  try {
    await fetch(url, {
      method: 'HEAD',
      cache: 'no-store',
      credentials: 'omit',
      mode: 'no-cors',
      signal: timeout.signal,
    })
    return true
  } catch {
    return false
  } finally {
    timeout.cancel()
  }
}

async function webReachable(): Promise<boolean> {
  const timeout = timeoutSignal(PROBE_TIMEOUT_MS)
  try {
    const response = await fetch(PROBE_PATH, {
      method: 'HEAD',
      cache: 'no-store',
      signal: timeout.signal,
    })
    return response.ok
  } catch {
    // Network error or timeout — server unreachable
    return false
  } finally {
    timeout.cancel()
  }
}

/**
 * Probe whether the server is actually reachable by fetching a lightweight
 * static resource. Returns true if the server responds within the timeout,
 * false otherwise. Never rejects.
 *
 * Results are cached for CACHE_TTL_MS to avoid hammering the server when
 * multiple sync triggers fire within the same window.
 */
export function probeConnectivity(): Promise<boolean> {
  const native = isNativePlatform()
  // Web fast exit, ahead of the cache: the browser says offline — no point probing,
  // and a "reachable" cached a moment ago must not outvote it.
  if (!native && !navigator.onLine) return Promise.resolve(false)

  if (cachedResult !== null && Date.now() - cachedAt < CACHE_TTL_MS) {
    return Promise.resolve(cachedResult)
  }
  if (inFlight) return inFlight

  const startedIn = generation
  const request = (native ? nativeReachable() : webReachable()).then(reachable => {
    if (startedIn === generation) {
      cachedResult = reachable
      cachedAt = Date.now()
    }
    return reachable
  })
  inFlight = request
  const clear = () => {
    if (inFlight === request) inFlight = null
  }
  void request.then(clear, clear)
  return request
}

/**
 * Invalidate the cached probe result. Call when a network event fires
 * (online, networkStatusChange) so the next probe does a real check.
 */
export function invalidateProbeCache(): void {
  generation += 1
  cachedResult = null
  cachedAt = 0
  inFlight = null
}
